import { type Breadcrumb, Severity } from '@sentry/browser';
import type { AxiosError, InternalAxiosRequestConfig, Method } from 'axios';

import { errorLogging } from '../errorLogging';

import {
  ApiResponseError,
  ClientApiError,
  type ProblemDetails,
  VersionMismatchError,
} from './apiError';
import type Headers from './headers';

export const includeAuthHeaderRequestInterceptor =
  (headerService: () => Headers, scopedBaseUrl: () => string) =>
  async (
    config: InternalAxiosRequestConfig,
  ): Promise<InternalAxiosRequestConfig> => {
    if (!(config.url || '').startsWith(scopedBaseUrl())) {
      return config;
    }

    const headers = await headerService().getAuthHeaders();
    config.headers.set(headers);

    return config;
  };

export const logRequestInterceptor = (
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig => {
  errorLogging().addBreadcrumb({
    type: 'http',
    level: Severity.Info,
    category: 'axios',
    data: {
      method: config.method,
      url: config.url,
    },
  } as HttpBreadcrumb);

  return config;
};

const isEmptyErrorResponse = (err: AxiosError): err is AxiosError<null> =>
  !err.response;

const isProblemDetailsResponse = (
  err: AxiosError,
): err is AxiosError<ProblemDetails> =>
  err.response &&
  err.response.headers['content-type'] &&
  err.response.headers['content-type'].startsWith('application/problem+json');

const logHttpBreadcrumb = (level: Severity, data: HttpData) => {
  errorLogging().addBreadcrumb({
    type: 'http',
    category: 'axios',
    level,
    data,
  } as HttpBreadcrumb);
};

export const logResponseErrorInterceptor = (err: AxiosError | Error): void => {
  if (!('config' in err)) {
    throw err;
  }
  const { method, url } = err.config!;

  if (isEmptyErrorResponse(err)) {
    logHttpBreadcrumb(Severity.Error, {
      method: method as Method,
      url,
      reason: 'No server response indicates a client-side network error',
    });
    throw new ClientApiError(err);
  }

  if (isProblemDetailsResponse(err)) {
    logHttpBreadcrumb(Severity.Error, {
      method: method as Method,
      url,
      reason: `${err.response?.data?.title}: ${err.response?.data?.detail}`,
      status_code: err.response?.data?.status,
    });
    if (
      err.response?.status === 412 &&
      err.response?.data.title === 'version-mismatch'
    ) {
      throw new VersionMismatchError(err);
    }

    throw new ApiResponseError(err);
  }

  throw err;
};

export interface HttpBreadcrumb extends Breadcrumb {
  type: 'http';
  data: HttpData;
}

export interface HttpData {
  method?: Method;
  url?: string;
  traceId?: string;
  reason?: string;
  status_code?: number;
}
